import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ALL_LANGS, splitLangPrefix, buildLocalizedPath } from "./utils/langRouting";

const SITE_NAME = "Art-of-Fart.com";
const SITE_URL = "https://www.art-of-fart.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/hero-bg.png`;

export default function SEO({
  title = `${SITE_NAME} – Record, Upload, Listen & Rate Farts!`,
  description = `Upload your funniest farts, listen to others, vote and have fun! Fart gallery, user profiles and ratings. Pure fun – 100% anonymous & free.`,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage = DEFAULT_OG_IMAGE,
  // Maße nur angeben, wenn sie bekannt sind (falsche Angaben verzerren Vorschauen)
  ogImageWidth,
  ogImageHeight,
  ogImageAlt = "Art-of-Fart.com – The funniest fart community on the internet",
  twitterTitle,
  twitterDescription,
  twitterImage,
  noindex = false,
  // false für nutzergenerierte Seiten (Furz/Profil): Sitemap und Server-HTML führen dort
  // bewusst kein hreflang – der Client darf nicht widersprechen.
  alternates = true,
  type = "website",
  lang,
}) {
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalTwitterTitle = twitterTitle || title;
  const finalTwitterDescription = twitterDescription || description;
  const finalTwitterImage = twitterImage || ogImage;
  // Standardbild public/hero-bg.png ist 1536×1024
  const imgW = ogImageWidth || (ogImage === DEFAULT_OG_IMAGE ? 1536 : null);
  const imgH = ogImageHeight || (ogImage === DEFAULT_OG_IMAGE ? 1024 : null);

  // Canonical + hreflang: abgeleitet aus der aktuellen Route, nicht aus dem
  // (weiterhin akzeptierten, aber ignorierten) ogUrl-Prop — dadurch stimmen
  // Canonical/hreflang auf jeder Route automatisch, ob mit oder ohne
  // Sprachpräfix (/de/gallery vs. /gallery), ohne die ~19 bestehenden
  // Aufrufstellen anpassen zu müssen. Siehe utils/langRouting.js.
  const location = useLocation();
  const { lang: urlLang, basePath } = splitLangPrefix(location.pathname);
  const currentLang = lang || urlLang;
  const canonicalUrl = `${SITE_URL}${buildLocalizedPath(currentLang, basePath)}`.replace(/\/$/, "") || SITE_URL;

  const hreflangLinks = !noindex && alternates
    ? [
        { hreflang: "x-default", href: `${SITE_URL}${buildLocalizedPath("en", basePath)}` },
        ...ALL_LANGS.map((l) => ({ hreflang: l, href: `${SITE_URL}${buildLocalizedPath(l, basePath)}` })),
      ]
    : [];

  // Locale mapping
  const localeMap = { en: "en_US", de: "de_DE", it: "it_IT", ja: "ja_JP", es: "es_ES", pt: "pt_BR", fr: "fr_FR", ru: "ru_RU", pl: "pl_PL", tr: "tr_TR" };
  const ogLocale = localeMap[currentLang] || "en_US";

  // WebSite-/Organization-JSON-LD steht einmal als @graph in public/index.html (gilt für
  // jede Seite). Das frühere zweite WebSite-Schema hier (anderer Name, SearchAction auf
  // einen nicht unterstützten ?search=-Parameter) ist entfernt.

  return (
    <Helmet>
      {/* Standard SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <html lang={currentLang} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang-Alternativen (keine auf noindex-Seiten) */}
      {hreflangLinks.map((l) => (
        <link key={l.hreflang} rel="alternate" hrefLang={l.hreflang} href={l.href} />
      ))}

      {/* Theme color for mobile browsers */}
      <meta name="theme-color" content="#fffbe7" />
      <meta name="msapplication-TileColor" content="#fffbe7" />

      {/* Robots */}
      {noindex && (
        <>
          <meta name="robots" content="noindex,nofollow" />
          <meta name="googlebot" content="noindex,nofollow" />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      {imgW && <meta property="og:image:width" content={String(imgW)} />}
      {imgH && <meta property="og:image:height" content={String(imgH)} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
    </Helmet>
  );
}
